import { useState } from 'react';

import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Link, useParams } from 'react-router-dom';

import { downloadUploadHistoryReport } from './report-utils';
import { SettingsHeader } from '../components/settings-header';
import { useGetMatterUploadHistory } from '@/api/queries/useGetMatterUploadHistory';
import { UploadMetadata } from '@/api/types/backend-dto-types';
import formatDate from '@/helpers/formatDate';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { ScrollableTable } from '@/new/components/table/scrollable-table';

const formatNumber = (num: number) => {
  return num.toLocaleString();
};

const fallbackUploadTableData: UploadMetadata[] = [];
const uploadColumnHelper = createColumnHelper<UploadMetadata>();

export function UploadHistory() {
  const { matterId } = useParams();
  if (!matterId) return null;

  const [globalFilter, setGlobalFilter] = useState('');
  const { data: uploads, isLoading } = useGetMatterUploadHistory(matterId);

  const uploadColumns = [
    uploadColumnHelper.display({
      id: 'index',
      header: () => <span>#</span>,
      cell: (info) => (
        <div className="flex h-7 w-7 items-center justify-center rounded bg-gray-100 text-xs font-semibold text-gray-700">
          {uploads ? uploads.length - info.row.index : '-'}
        </div>
      ),
    }),
    uploadColumnHelper.accessor('successfullyUploadedFileCount', {
      header: () => <span>Files Uploaded</span>,
      cell: (info) => <span className="text-sm font-medium text-gray-800">{formatNumber(info.getValue() ?? 0)}</span>,
    }),
    uploadColumnHelper.accessor('createdBy.email', {
      header: () => <span>Created By</span>,
      cell: (info) => <span className="text-xs text-gray-900">{info.getValue()}</span>,
    }),
    uploadColumnHelper.accessor('createdAt', {
      header: () => <span>Upload Date</span>,
      cell: (info) => (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Icon className="lucide--calendar h-3 w-3" />
          <span>{formatDate(info.getValue(), true, true)}</span>
        </div>
      ),
    }),
    uploadColumnHelper.accessor('uploadFailures', {
      header: () => <span>Failures</span>,
      cell: (info) => {
        const failures = info.getValue();
        if (failures.length === 0) return <span className="text-xs text-gray-400">0</span>;

        return (
          <div className="flex items-center gap-1 text-sm font-medium text-red-600">
            <Icon className="lucide--alert-triangle h-3 w-3" />
            {formatNumber(failures.length)}
          </div>
        );
      },
    }),
    uploadColumnHelper.display({
      id: 'actions',
      header: () => <span></span>,
      cell: (info) => (
        <Button variant="outline" size="sm" asChild className="group">
          <Link to={`/matters/${matterId}/settings/upload-history/${info.row.original.id}`}>
            View
            <Icon className="lucide--arrow-right h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.5" />
          </Link>
        </Button>
      ),
    }),
  ];

  const table = useReactTable({
    data: uploads ?? fallbackUploadTableData,
    columns: uploadColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  return (
    <>
      <SettingsHeader
        title="Upload History"
        subheading={
          <>
            <p className="text-gray-500">
              View all document uploads for this matter, including any files that failed to upload.
            </p>
            <p className="mt-2 text-xs text-gray-500">
              An upload is when you attempt to upload files to Wexler, not when you submit them for processing.
              Submitting files is a <b>Run</b>.
            </p>
          </>
        }
      />

      <ScrollableTable
        table={table}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        plural="uploads"
        loading={isLoading}
        className="p-0"
        additionalButtons={
          <div className="ml-auto">
            <Button
              variant="default"
              size="default"
              onClick={() => uploads && downloadUploadHistoryReport(uploads, matterId)}
              disabled={!uploads || uploads.length === 0}
            >
              <Icon className="lucide--download h-4 w-4" />
              Download report
            </Button>
          </div>
        }
      />
    </>
  );
}
